import { useState } from "react";
import { Loader2, Phone, Mail, CheckCircle, XCircle } from "lucide-react";
import { Chip } from "@mui/material";
import {
  useGetAllinformationByid,
  usePostVerifyRejectApplicationById,
} from "../../adapters/Requests";
import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import ApplicantPhoto from "./ApplicantPhoto";
import { applicationStatuses } from "../../utils/constants";
import { useFlashMessageContext } from "../../contexts/FlashMessageContext";

function CandidateCard({ applicationId: propAppId, onActionComplete }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [openRejection, setOpenRejection] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [comment, setComment] = useState("");

  const params = useParams();
  const application_id = propAppId || params.application_id;

  const { data, isLoading, isError } = useGetAllinformationByid({
    application_id,
  });
  const verifyRejectApplication = usePostVerifyRejectApplicationById();

  const { INVALIDATED, RETURNED, VERIFIED, APPROVED, REJECTED } =
    applicationStatuses;
  const { setOpenFlash, setSeverity, setFlashMessage } =
    useFlashMessageContext();

  if (isError) return <div>Error reload or go back</div>;

  const handleVerify = () => setOpenDialog(true);

  const displayStatus = ["APPROVED", "ACCEPTED", "REJECTED", "DECLINED", "COMPLETED"].includes(data?.status)
    ? VERIFIED
    : data?.status;

  const handleMarkStatus = async (status_new) => {
    setIsConfirming(true);
    const formData = { application_id, status: status_new };

    if (INVALIDATED === status_new) {
      formData.comment = comment;
    }

    try {
      const response = await verifyRejectApplication.mutateAsync({
        formData: formData,
      });
      setFlashMessage(response.message);
      setSeverity("success");
      setOpenFlash(true);
      onActionComplete?.();
    } catch (error) {
      console.error("Error marking as verified:", error);
      setFlashMessage("Something went wrong!");
      setSeverity("error");
      setOpenFlash(true);
    } finally {
      handleCommentDialogClosure();
      handleCloseDialog();
      handleCloseRejection();
      setIsConfirming(false);
    }
  };

  const handleCloseDialog = () => setOpenDialog(false);
  const handleCommentDialogClosure = () => {
    setOpenCommentDialog(false);
    setComment("");
  };
  const handleCommentSubmit = () => {
    if (comment.length < 3) return;
    setOpenRejection(true);
  };
  const handleCloseRejection = () => setOpenRejection(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 w-full">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : data ? (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <ApplicantPhoto applicationId={application_id} />

          <div className="flex flex-col items-end gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2">
              {displayStatus === VERIFIED ? (
                <Chip color="success" label={VERIFIED} variant="filled" />
              ) : displayStatus === APPROVED ? (
                <Chip color="success" label="Approved" variant="filled" />
              ) : displayStatus === INVALIDATED ? (
                <Chip color="error" label={INVALIDATED} variant="filled" />
              ) : displayStatus === REJECTED ? (
                <Chip color="error" label={REJECTED} variant="filled" />
              ) : displayStatus === RETURNED ? (
                <Chip label={RETURNED} variant="filled" />
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleVerify}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setOpenCommentDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Invalidate
                  </Button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-500" />
                <span className="font-medium">
                  {data.Applicant.phone_number}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-500" />
                <span className="font-medium">{data.Applicant.email}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">No data available</div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Verification</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this application as verified?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleMarkStatus(VERIFIED)}
              disabled={isConfirming}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {isConfirming ? "Verifying..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openRejection} onOpenChange={setOpenRejection}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Invalidation</DialogTitle>
            <DialogDescription>
              Are you sure you want to invalidate this application?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseRejection}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleMarkStatus(INVALIDATED)}
              disabled={isConfirming}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {isConfirming ? "Invalidating..." : "Invalidate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCommentDialog} onOpenChange={setOpenCommentDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invalidation Comment</DialogTitle>
            <DialogDescription>
              Please provide a reason for invalidating this application.
            </DialogDescription>
          </DialogHeader>
          <textarea
            rows={4}
            className="w-full resize-none border border-slate-200 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            placeholder="Enter comment here..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCommentDialogClosure}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCommentSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CandidateCard;
